import React, { useState } from 'react';
import TaskOverrideModal from './TaskOverrideModal';
import './TaskIdentificationCard.css';

interface TaskIdentificationCardProps {
  taskIdentification: any;
  onOverride?: (domain: string, task: string) => void;
}

const TaskIdentificationCard: React.FC<TaskIdentificationCardProps> = ({
  taskIdentification,
  onOverride
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  
  // Extract values from taskIdentification
  const domain = taskIdentification?.domain;
  const task = taskIdentification?.task;
  const method = taskIdentification?.method || 'ai';
  const timestamp = taskIdentification?.timestamp;

  const getMethodLabel = () => {
    switch (method) {
      case 'ai':
        return 'AI Identification';
      case 'regex':
        return 'Pattern Matching';
      case 'manual':
        return 'Manual Selection';
      default:
        return 'Unknown';
    }
  };

  const getMethodClass = () => {
    switch (method) {
      case 'ai':
        return 'method-ai';
      case 'regex':
        return 'method-regex';
      case 'manual':
        return 'method-manual';
      default:
        return '';
    }
  };

  const formatTaskName = (taskName: string) => {
    return taskName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const formatDomainName = (domainName: string) => {
    const domainDescriptions: Record<string, string> = {
      treasury: 'Treasury & Cash Management',
      revenue: 'Revenue & Sales',
      expense: 'Expenses & Purchasing',
      gl: 'General Ledger',
      ar: 'Accounts Receivable',
      ap: 'Accounts Payable',
      payroll: 'Payroll',
      tax: 'Tax',
      inventory: 'Inventory'
    };
    return domainDescriptions[domainName.toLowerCase()] || formatTaskName(domainName);
  };

  if (!domain && !task) {
    return null;
  }

  return (
    <div className={`task-identification-card ${getMethodClass()}`}>
      <div className="task-id-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="task-id-main">
          {domain && (
            <div className="task-id-item">
              <span className="task-id-icon">📊</span>
              <span className="task-id-label">Domain:</span>
              <span className="task-id-value">{formatDomainName(domain)}</span>
            </div>
          )}
          {task && (
            <div className="task-id-item">
              <span className="task-id-icon">📋</span>
              <span className="task-id-label">Task:</span>
              <span className="task-id-value">{formatTaskName(task)}</span>
            </div>
          )}
        </div>
        <div className="task-id-actions">
          {onOverride && (
            <button 
              className="task-override-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowOverrideModal(true);
              }}
              title="Change task selection"
            >
              Wrong task?
            </button>
          )}
          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
            ▼
          </span>
        </div>
      </div>
      
      {isExpanded && (
        <div className="task-id-details">
          <div className="task-id-detail-item">
            <span className="detail-label">Method:</span>
            <span className="detail-value">{getMethodLabel()}</span>
          </div>
          {timestamp && (
            <div className="task-id-detail-item">
              <span className="detail-label">Identified at:</span>
              <span className="detail-value">{new Date(timestamp).toLocaleTimeString()}</span>
            </div>
          )}
          <div className="task-id-info">
            <p className="info-text">
              Assertion will use the <strong>{formatTaskName(task || '')}</strong> framework 
              from the <strong>{formatDomainName(domain || '')}</strong> domain to process your request.
            </p>
            {method === 'regex' && (
              <p className="warning-text">
                ⚠️ Identified using pattern matching. AI identification was not available.
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Task Override Modal */}
      <TaskOverrideModal
        isOpen={showOverrideModal}
        currentDomain={domain}
        currentTask={task}
        onConfirm={(newDomain, newTask) => {
          console.log('[TaskIdentificationCard] Override confirmed:', newDomain, newTask);
          setShowOverrideModal(false);
          onOverride?.(newDomain, newTask);
        }}
        onCancel={() => setShowOverrideModal(false)}
      />
    </div>
  );
};

export default TaskIdentificationCard;